export type NotificationType = 'success' | 'error' | 'warning' | 'info';

class NotificationService {
  private container: HTMLDivElement | null = null;
  private notifications: Map<string, HTMLDivElement> = new Map();

  constructor() {
    this.initContainer();
  }

  private initContainer() {
    if (typeof window === 'undefined') return;
    
    this.container = document.createElement('div');
    this.container.id = 'notification-container';
    this.container.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 10px;
      pointer-events: none;
    `;
    document.body.appendChild(this.container);
  }

  show(message: string, type: NotificationType = 'info', duration: number = 5000) {
    if (!this.container) this.initContainer();
    
    const id = Date.now().toString();
    const notification = document.createElement('div');
    notification.id = id;
    
    const colors = {
      success: { bg: '#10b981', text: '#ffffff' },
      error: { bg: '#ef4444', text: '#ffffff' },
      warning: { bg: '#f59e0b', text: '#ffffff' },
      info: { bg: '#3b82f6', text: '#ffffff' }
    };
    
    const color = colors[type];
    
    notification.style.cssText = `
      padding: 16px 24px;
      background: ${color.bg};
      color: ${color.text};
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
      font-size: 14px;
      font-weight: 500;
      max-width: 400px;
      word-wrap: break-word;
      animation: slideIn 0.3s ease-out;
      pointer-events: auto;
      cursor: pointer;
    `;
    
    notification.textContent = message;
    notification.onclick = () => this.remove(id);
    
    // Add animation styles
    if (!document.getElementById('notification-styles')) {
      const style = document.createElement('style');
      style.id = 'notification-styles';
      style.textContent = `
        @keyframes slideIn {
          from {
            transform: translateX(100%);
            opacity: 0;
          }
          to {
            transform: translateX(0);
            opacity: 1;
          }
        }
        @keyframes slideOut {
          from {
            transform: translateX(0);
            opacity: 1;
          }
          to {
            transform: translateX(100%);
            opacity: 0;
          }
        }
      `;
      document.head.appendChild(style);
    }
    
    this.container?.appendChild(notification);
    this.notifications.set(id, notification);
    
    // Auto remove after duration
    if (duration > 0) {
      setTimeout(() => this.remove(id), duration);
    }
    
    return id;
  }

  update(id: string, message: string) {
    const notification = this.notifications.get(id);
    if (notification) {
      notification.textContent = message;
    }
  }

  remove(id: string) {
    const notification = this.notifications.get(id);
    if (notification) {
      notification.style.animation = 'slideOut 0.3s ease-out';
      setTimeout(() => {
        notification.remove();
        this.notifications.delete(id);
      }, 300);
    }
  }

  success(message: string, duration?: number) {
    return this.show(message, 'success', duration);
  }

  error(message: string, duration?: number) {
    return this.show(message, 'error', duration);
  }

  warning(message: string, duration?: number) {
    return this.show(message, 'warning', duration);
  }

  info(message: string, duration?: number) {
    return this.show(message, 'info', duration);
  }

  clear() {
    this.notifications.forEach((_, id) => this.remove(id));
  }
}

// Create singleton instance
const notificationService = new NotificationService();

export default notificationService;